import { getListDataFunctionFactory } from '../../modules/getListDataFunctionFactory'
import {
  getCompanyContactListData,
  _getCCTDataById,
} from '../companyContacts/mockData'
import { getCaseNoteListDataByCaseId } from '../caseNotes/mockData'
import { getCaseDocumentListDataByCaseId } from '../caseDocuments/mockData'
import { getCaseEmailListDataByCaseId } from '../caseEmails/mockData'
import { getTaskListDataByCaseId } from '../tasks/mockData'
import { getCompanyMSServiceDataById } from '../companyMSServices/mockData'
import { CaseStatusIds } from '~/services/selectLists/caseStatii'
import { CaseSource } from '@/types/case/models'
import type { ICase, ICaseOverview } from '@/types/case/models'
import type { ICompanyMSService } from '~/types/company/models'
import type { TEntityId } from '~/types/models'

const mn = '[api/msw/api/cases/mockData.ts]'

const caseListData = undefined as unknown as ICase[]

/**
 * Replace a case's stored `{id, label}` with the WHOLE companyMSService.
 *
 * `ICase.companyMSService` is an `ICompanyMSService`, not an
 * `IEntitySelectItem`: the Cases card renders columns off the service itself
 * -- `companyMSService.keyIdentifierValue`, the value that tells five
 * identical POTS lines apart -- and a select item carries only a label.
 *
 * WHY RESOLVED ON READ RATHER THAN STORED WHOLE. The seed keeps the id and
 * label a user's pick actually sends, and this re-reads the live row on every
 * list read, so a case can never show a stale copy of a service that has since
 * been edited. It also means the seed does not carry four near-duplicate
 * msServices. Same idiom as companyMasterServices' own forOfFunction, which
 * attaches companyMSServices the same way.
 *
 * ⚠ Going through `getCompanyMSServiceDataById` is deliberate: that function
 * sets `keyIdentifierValue` from the product's fieldSet
 * (api/msw/modules/setKeyIdentifierValues.ts), so the resolved service arrives
 * with the key identifier already on it.
 *
 * ⚠ A case may point at an id that no longer exists -- the msService was
 * deleted, or mock data was reset while cases were persisted. That must not
 * take out the whole Cases list, so the row keeps whatever it already had.
 */
async function _resolveCompanyMSService(theCase: ICase) {
  const fn = `${mn} _resolveCompanyMSService |`
  const companyMSServiceId = theCase.companyMSService?.id
  if (!companyMSServiceId) {
    return
  }
  try {
    theCase.companyMSService = await getCompanyMSServiceDataById(
      companyMSServiceId
    )
  } catch (e) {
    console.debug(
      `${fn} case ${theCase.hrid} points at companyMSService` +
        ` '${companyMSServiceId}', which does not exist. Leaving as is.`,
      e
    )
  }
}

export const getCaseListData = getListDataFunctionFactory<ICase[]>({
  listData: caseListData,
  storageKey: 'cases',
  getStartingListData: getStartingCaseListData,
  forOfFunction: async (theCase) => {
    await _resolveCompanyMSService(theCase as ICase)
  },
})

/**
 * A case's companyMSService AS STORED: the `{id, label}` a user's pick sends.
 *
 * ⚠ The cast is the honest description of a real asymmetry, not a shortcut.
 * `ICase.companyMSService` types what a READ returns -- the whole service,
 * assembled by `_resolveCompanyMSService` above -- while a WRITE puts only the
 * select item there, because that is all a `doReturnObject` select emits
 * (services/entityInputFieldsService/modules/case.ts). The seed is the stored
 * half, so it is shaped like a write, and every read resolves it.
 */
function storedCompanyMSService(
  id: TEntityId,
  label: string
): ICompanyMSService {
  return { id, label } as unknown as ICompanyMSService
}

/**
 * ⚠ A CASE IS ABOUT ONE ACTIVE SERVICE, OR NONE YET. The Anchor Entity choice
 * (service / supplier / other) was removed from Cases; Service Requests keep it
 * (api/msw/api/serviceRequests/mockData.ts). The service stays optional -- an
 * emailed case may never have one identified.
 *
 * CA0001/CA0002/CA0003 carry a service. CA0004 is the LEGACY shape on purpose:
 * a supplier and no service, as a case created while the Supplier anchor existed
 * still is (1 on staging at the removal). It keeps the legacy display -- the
 * supplier shown from `supplier` -- and "choosing a service clears it"
 * exercised. CA0005 and CA0006 carry neither. CA0007 names JUST A COMPANY
 * (cynque-api-server #1750): `company` and `companyMSService` are exclusive,
 * so every other seed spells its `company` null.
 *
 * ⚠ An absent FK is spelled `null`, not omitted. Every ICase property is
 * required-and-nullable, which is the same contract the server enforces on a
 * create: present, with null where there is no value.
 */
export async function getStartingCaseListData(): Promise<ICase[]> {
  const cctList = await getCompanyContactListData()
  return [
    {
      id: 'XXCA0001',
      hrid: 'CA0001',
      descr: 'POTS line at Boston dropping calls',
      openedAt: '2022-03-01T14:05:12.418302Z',
      createdAt: '2022-03-01T14:05:12.418302Z',
      type: { id: 'XXCAT0001', label: 'Service Down' },
      numLocationsAffected: 1,
      contact: _getCCTDataById('XXCCT0017', cctList) ?? null,
      companyMSService: storedCompanyMSService('XXCMSS0096', 'POTS - Boston'),
      company: null,
      supplier: null,
      assignedTo: {
        id: 'XXTM0006',
        label: 'Service Technician',
      },
      status: { id: 6, label: 'Escalated' },
      source: CaseSource.MANUAL,
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXCA0002',
      hrid: 'CA0002',
      descr: 'Static on the Boston fax line',
      openedAt: '2022-03-10T15:32:47.093114Z',
      createdAt: '2022-03-10T15:32:47.093114Z',
      type: { id: 'XXCAT0001', label: 'Service Down' },
      numLocationsAffected: 1,
      contact: _getCCTDataById('XXCCT0017', cctList) ?? null,
      companyMSService: storedCompanyMSService('XXCMSS0097', 'POTS - Boston'),
      company: null,
      supplier: null,
      assignedTo: {
        id: 'XXTM0006',
        label: 'Service Technician',
      },
      status: { id: 5, label: 'Active (Other)' },
      source: CaseSource.MANUAL,
      version: '1',
      canBeDeleted: true,
    },
    {
      id: 'XXCA0003',
      hrid: 'CA0003',
      descr: 'No dial tone after overnight power cut',
      openedAt: '2022-03-06T16:48:03.771950Z',
      createdAt: '2022-03-06T16:48:03.771950Z',
      type: { id: 'XXCAT0001', label: 'Service Down' },
      numLocationsAffected: 1,
      contact: _getCCTDataById('XXCCT0017', cctList) ?? null,
      // Supplier-anchored until the anchor choice was removed; now about the
      // service, like any case. (Its Service Request copy keeps the supplier.)
      companyMSService: storedCompanyMSService('XXCMSS0096', 'POTS - Boston'),
      company: null,
      supplier: null,
      assignedTo: {
        id: 'XXTM0006',
        label: 'Service Technician',
      },
      status: { id: 2, label: 'Pending Cust Info' },
      source: CaseSource.MANUAL,
      version: '1',
      canBeDeleted: false,
    },
    {
      id: 'XXCA0004',
      hrid: 'CA0004',
      descr: 'Intermittent outbound call failures',
      openedAt: '2022-03-02T13:21:39.250006Z',
      createdAt: '2022-03-02T13:21:39.250006Z',
      type: { id: 'XXCAT0003', label: 'Service Intermittent' },
      numLocationsAffected: 1,
      contact: _getCCTDataById('XXCCT0017', cctList) ?? null,
      // ⚠ LEGACY on purpose -- see the note above this function.
      companyMSService: null,
      company: null,
      supplier: {
        id: 'XXS0009',
        label: 'PacTel',
      },
      assignedTo: {
        id: 'XXTM0006',
        label: 'Service Technician',
      },
      status: { id: 3, label: 'Pending Dispatch' },
      source: CaseSource.MANUAL,
      version: '1',
      canBeDeleted: false,
    },
    {
      // ⚠ NO SERVICE, SETTLED. Neither a supplier nor a service, and a status
      // that is NOT `From Email / Needs Review` -- so `isAwaitingTriage` is
      // false and Case Type stays hidden on edit. (It was the "Other anchor"
      // fixture until the anchor choice was removed; CA0005/CA0006 also carry
      // neither FK, but they are awaiting review.)
      //
      // `numLocationsAffected` is null: it was hidden under the Other anchor,
      // and nobody has answered it since.
      //
      // Status `Active (In Progress)` is the WIDEST case-status label, and the
      // one the Status column on CasesCard is sized from. Nothing else rendered
      // it, so the column's binding constraint went unseen on screen.
      id: 'XXCA0007',
      hrid: 'CA0007',
      descr: 'Update the billing contact on our account',
      openedAt: '2022-03-16T17:09:54.602873Z',
      createdAt: '2022-03-16T17:09:54.602873Z',
      type: { id: 'XXCAT0001', label: 'Service Down' },
      numLocationsAffected: null,
      contact: _getCCTDataById('XXCCT0017', cctList) ?? null,
      companyMSService: null,
      // JUST A COMPANY: a case opened before anyone knows which of the
      // company's services it is about (cynque-api-server #1750). The only
      // seeded one, so the Company column and the edit form have one to show.
      company: { id: 'XXC0001', label: 'Metropolis Bank' },
      supplier: null,
      assignedTo: {
        id: 'XXTM0006',
        label: 'Service Technician',
      },
      status: { id: 4, label: 'Active (In Progress)' },
      source: CaseSource.MANUAL,
      version: '1',
      canBeDeleted: true,
    },
    // ─────────────────────────────────────────────────────────────────────
    // THE THREE STATUSES NOTHING ELSE REACHES: Resolved, Canceled, Paused.
    //
    // Seeded so the Show-For-Status menu is demonstrable end to end -- before
    // these, three of its eleven options returned an empty list, and 'All Open'
    // was indistinguishable from 'All' because no case was ever closed. A
    // filter that cannot be seen excluding anything is a filter nobody can
    // check.
    //
    // ⚠ Resolved and Canceled are the whole of TERMINAL_CASE_STATUS_IDS, so
    // these two are what 'All Open' must hide. Paused is the third on purpose:
    // it is the judgement call in that list -- work stopped, the case did not
    // end -- so it must STAY in 'All Open', and only a seeded row shows that.
    // ─────────────────────────────────────────────────────────────────────
    {
      // Closed out: the service was restored and the case finished normally.
      // Service-anchored, like the case it mirrors (CA0001), so a terminal case
      // is not also an odd shape in every other respect.
      id: 'XXCA0008',
      hrid: 'CA0008',
      descr: 'Dropped calls on the Boston POTS line',
      openedAt: '2022-02-02T14:56:21.938417Z',
      createdAt: '2022-02-02T14:56:21.938417Z',
      type: { id: 'XXCAT0001', label: 'Service Down' },
      numLocationsAffected: 1,
      contact: _getCCTDataById('XXCCT0017', cctList) ?? null,
      companyMSService: storedCompanyMSService('XXCMSS0096', 'POTS - Boston'),
      company: null,
      supplier: null,
      assignedTo: {
        id: 'XXTM0006',
        label: 'Service Technician',
      },
      status: { id: 9, label: 'Resolved' },
      source: CaseSource.MANUAL,
      version: '1',
      canBeDeleted: true,
    },
    {
      // Withdrawn by the customer before anyone was dispatched. Supplier-
      // anchored, so the second terminal status is not a clone of the first.
      id: 'XXCA0009',
      hrid: 'CA0009',
      descr: 'Requested a survey, then withdrew the request',
      openedAt: '2022-02-11T15:13:08.144529Z',
      createdAt: '2022-02-11T15:13:08.144529Z',
      type: { id: 'XXCAT0001', label: 'Service Down' },
      numLocationsAffected: null,
      contact: _getCCTDataById('XXCCT0017', cctList) ?? null,
      companyMSService: null,
      company: null,
      supplier: {
        id: 'XXS0009',
        label: 'PacTel',
      },
      assignedTo: null,
      status: { id: 8, label: 'Canceled' },
      source: CaseSource.MANUAL,
      version: '1',
      canBeDeleted: true,
    },
    {
      // ⚠ PAUSED IS OPEN. Waiting on hardware, not finished -- so this row must
      // appear under 'All Open' alongside the active ones. It is the only case
      // that demonstrates isOpenCaseStatus's one judgement call on screen.
      id: 'XXCA0010',
      hrid: 'CA0010',
      descr: 'Replacement handset on back-order from the supplier',
      openedAt: '2022-03-18T16:27:45.507391Z',
      createdAt: '2022-03-18T16:27:45.507391Z',
      type: { id: 'XXCAT0001', label: 'Service Down' },
      numLocationsAffected: 1,
      contact: _getCCTDataById('XXCCT0017', cctList) ?? null,
      companyMSService: storedCompanyMSService('XXCMSS0097', 'POTS - Boston'),
      company: null,
      supplier: null,
      assignedTo: {
        id: 'XXTM0006',
        label: 'Service Technician',
      },
      status: { id: 7, label: 'Paused' },
      source: CaseSource.MANUAL,
      version: '1',
      canBeDeleted: true,
    },
    // ─────────────────────────────────────────────────────────────────────
    // Created by EMAIL-TO-CASE, and not yet triaged by anyone.
    //
    // ⚠ The pathway knows almost nothing. An inbound message arrives at the
    // support address, a case is opened from it and its body is stored as a
    // note authored by Case Bot -- but nothing in that flow can say which
    // service the customer means, so `companyMSService` (and `supplier`) are
    // null and `assignedTo` is null until a human picks it up. That
    // is a state no amount of clicking in the app produces, which is why it
    // has to be seeded: the four cases above were all authored as though a
    // person filled the dialog in.
    //
    // Both carry status 1, `From Email / Needs Review`. That status exists to
    // cue a human to set the Case Type and, if it can be found, the service
    // the message is about -- which may never be found, so a triaged case can
    // move on to a worked status with no service at all.
    //
    // `CaseSource.EMAIL` is the DURABLE marker of the pathway (issue #1635) and
    // is what tells the two apart from a manually-raised case forever. The
    // status does not: it stops being `From Email / Needs Review` the moment
    // someone triages. Two markers, two different questions.
    //
    // `descr` is the SUBJECT LINE, because that is the only name the pathway
    // has for the case.
    // ─────────────────────────────────────────────────────────────────────
    {
      // Sender RESOLVED to a known contact -- Bilbo Baggins at Boston Coach,
      // matched on his address.
      id: 'XXCA0005',
      hrid: 'CA0005',
      descr: 'Static on every call from the Sunset office',
      openedAt: '2022-03-12T13:44:30.861208Z',
      createdAt: '2022-03-12T13:44:30.861208Z',
      type: null,
      numLocationsAffected: null,
      contact: _getCCTDataById('XXCCT0031', cctList) ?? null,
      companyMSService: null,
      company: null,
      supplier: null,
      assignedTo: null,
      status: { id: 1, label: 'From Email\nNeeds Review' },
      source: CaseSource.EMAIL,
      version: '1',
      canBeDeleted: true,
    },
    {
      // Sender NOT known -- nobody in the system has this address, so the
      // pathway leaves `contact` null rather than guessing. ⚠ The one seeded
      // case with no contact at all, and therefore the only one that exercises
      // Case()'s null-contact guard on the Cases list, where the factory runs
      // per row inside a bare forEach with no try/catch.
      //
      // ⚠ `type` is null, as are `companyMSService`, `supplier` and `assignedTo`: that is exactly
      // what case_create_from_email.py creates. It validates a Case with only
      // id / descr / openedAt / status and lets every other column default,
      // so a case cannot arrive from email already knowing its type. The second
      // case TYPE moved to CA0007 to keep that coverage.
      id: 'XXCA0006',
      hrid: 'CA0006',
      descr: 'Please update the billing address on our account',
      openedAt: '2022-03-14T18:02:16.329740Z',
      createdAt: '2022-03-14T18:02:16.329740Z',
      type: null,
      numLocationsAffected: null,
      contact: null,
      companyMSService: null,
      company: null,
      supplier: null,
      assignedTo: null,
      status: { id: 1, label: 'From Email\nNeeds Review' },
      source: CaseSource.EMAIL,
      version: '1',
      canBeDeleted: true,
    },
  ]
}

export async function getCaseDataById(
  id: TEntityId
): Promise<ICase | undefined> {
  const list = await getCaseListData()
  return list?.find((caseData) => caseData.id === id)
}

// Every case still awaiting triage, whoever it is assigned to -- the same list
// for every team member, as with getUnassignedOrderListData. The webapp shows
// it only to a CASES_MANAGER (cynque-api-server#1741).
export async function getFromEmailNeedsReviewCaseListData(): Promise<ICase[]> {
  const list = await getCaseListData()
  if (!list) {
    throw new Error(`getCaseListData returned undefined/null`)
  }
  return list.filter(
    (caseData: ICase) =>
      caseData.status?.id === CaseStatusIds.FROM_EMAIL_NEEDS_REVIEW
  )
}

/**
 * The case overview payload, mirroring getSupplierOverviewData.
 *
 * ⚠ NO `lastSelectedState` key, and that is deliberate -- unlike every other
 * overview payload in this app. There is no /api/cases/{id}/lastselectedstate
 * resource, CaseOverview declares no such field, and the Case overview is a
 * single tabless page with no tab selection to persist. Do not "fix" this by
 * adding one: see the note in api/msw/api/suppliers/mockData.ts for what the
 * ABSENCE of the key costs on a silo that genuinely needs it, and the page's own
 * comment for why Cases does not.
 *
 * ⚠ `caseEmails` includes rows whose `body` is null (auto-replies, bounces,
 * spam). The server returns them too; the Emails card must filter them rather
 * than the payload hiding them.
 */
export async function getCaseOverviewData(
  id: TEntityId
): Promise<ICaseOverview | undefined> {
  const caseData = await getCaseDataById(id)
  if (!caseData) {
    return undefined
  }
  return {
    // ⚠ NO standalone `contact` key. There used to be one, because the server
    // built the two from different views and only the overview's was whole.
    // `case.contact` is a whole TContact now, so this payload carries exactly
    // one contact -- and the mock must NOT put the same object at two keys
    // again: that shared reference is what made the old split invisible
    // offline while the real API returned two different objects.
    case: caseData,
    caseNotes: (await getCaseNoteListDataByCaseId(id)) || [],
    caseDocuments: (await getCaseDocumentListDataByCaseId(id)) || [],
    caseEmails: (await getCaseEmailListDataByCaseId(id)) || [],
    tasks: await getTaskListDataByCaseId(id),
  }
}
